import { ChangeDetectionStrategy, Component, OnInit, signal } from "@angular/core";
import skjemarammeverk from '../../../../assets/schema/skjemarammeverk.json';
import { MarkerModule } from "@colsen1991/ngx-translate-extract-marker/extras";
import {
  Accordion,
  FormattedText,
  FormEvent,
  Rule,
  RzrForms,
  Skjema,
  SkjemaDef,
  SkjemaRenderState,
  Sporvalg,
  Steg,
  TextAreaField,
  TextField,
  VedleggKrav
} from "@husbanken/rzrforms-lib";
import { ActivatedRoute, Router } from "@angular/router";
import { SikkerInnsendingDataTo } from "../../../../../projects/skjemarammeverk/models/sikker-innsending-data-to";
import { marker as _ } from "@colsen1991/ngx-translate-extract-marker";
import { ValideringsRegler } from "@husbanken/designsystem-angular-forms";
import { SikkerInnsendingService } from "../../../../../projects/api/services/sikker-innsending.service";
import { LinkComponent } from "./link.component";
import { InternalVedleggkravRenderer } from "./vedleggskrav/internal.vedleggkrav.renderer";
import { BehaviorSubject } from "rxjs";

@Component({
  selector: "senddokument-skjemautfylling",
  templateUrl: "./skjemarammeverk.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: "skjemarammeverk.component.scss",
  imports: [
    MarkerModule,
    RzrForms,
  ],
})
export class SkjemarammeverkComponent implements OnInit {
  myUuid = signal("");
  realm = "";
  loading = signal(false);
  public skjemaRenderState$ = new BehaviorSubject<SkjemaRenderState>("edit");

  constructor(private router: Router, private activatedRoute: ActivatedRoute, private sikkerInnsendingService: SikkerInnsendingService) {
  }

  ngOnInit(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const uuidFromParam = urlParams.get('uuid');
    this.realm = this.findRealm();

    if (uuidFromParam == null) {
      this.sikkerInnsendingService.createNewSchema().subscribe(val => {
        this.myUuid.set(val.uuid)
        this.router.navigate([], {
          replaceUrl: true,
          relativeTo: this.activatedRoute,
          queryParams: {uuid: val.uuid},
          queryParamsHandling: "merge"
        })
      })
    } else {
      this.myUuid.set(uuidFromParam)
    }
  }

  skjemaRenderState(state: SkjemaRenderState) {
    this.skjemaRenderState$.next(state);
  }

  formDef = new SkjemaDef("Sikker-innsending", skjemarammeverk, skjemarammeverk.components.schemas.SikkerInnsendingDataTO,
    () => {
      return new Skjema<SikkerInnsendingDataTo>(skjema => {
        skjema.bunntekst(null)
        skjema.element(new Sporvalg(sporvalg => {
          sporvalg.steg(new Steg({id: "digital", label: _("Send dokumenter digitalt")}, steg => {
            steg.element(new FormattedText(`<p class="hb-text--ingress hb-mb--xl">{{${_("Her kan du sende inn dokumenter til Husbanken på en sikker måte.")}}}</p>`))
            steg.element(new Accordion(ac => {
              ac.label(_("Vi vil hente og bruke informasjon om deg"))
              ac.element(new FormattedText(`<p>{{${_("Husbanken kan behandle personopplysninger som er nødvendige for å saksbehandle og forvalte våre lån- og tilskuddsordninger, jf. § 10 i Husbankloven.")}}}</p>
                          <p>{{${_("I tillegg til den informasjonen du legger inn i skjemaet, henter vi:")}}}</p>
                          <ul class="hb-list hb-mb--md">
                          ` + (this.realm == "proff" ? `
                              <li>{{${_("Organisasjonsnummeret til virksomheten du representerer")}}}</li>`
                  : ``) +
                `<li>{{${_("Fødselsnummeret ditt")}}}</li>
                              <li>{{${_("Navnet ditt")}}}</li>
                              <li>{{${_("E-postadressen din")}}}</li>
                              <li>{{${_("Ved behov kan vi også behandle opplysninger om deg som vi har fra før")}}}</li>
                          </ul>
                          <div class="hb-mt--md">
                              <a href="https://husbanken.no/om-husbanken/personvern/"
                                 target="_blank" class="hb-interactive">
                                  <span>{{${_("Les om rettighetene dine (åpnes i nytt vindu)")}}}</span>
                                  <hb-icon class="hb-mb--xs hb-ml--xs" size="Size100" iconId="expand-6"></hb-icon>
                              </a>
                          </div>`))
            }))
            steg.field("tittel", new TextField(f => {
              f.label(_("Tittel"))
              f.required(_("Skriv en tittel som beskriver hva det gjelder"))
              f.fieldDescription(_("Maks 200 tegn"))
              f.valid(c => {
                if (c.getValue() && c.getValue().length > 200) {
                  return {literal: _("Tittelen kan ikke være lengre enn 200 tegn")};
                }
                return
              })
              f.fullBredde()
            }))
            steg.field("melding", new TextAreaField(f => {
              f.label(_("Melding"))
              f.fieldDescription(_("Beskriv hva henvendelsen gjelder. Oppgi saksnummer dersom du har det."))
              f.required(_("Skriv en melding om hva det gjelder"))
            }))
            steg.field("vedlegg", new VedleggKrav(vk => {
              vk.opplastingLabel(_("Vedlegg"))
              vk.notRequired()
              vk.customRenderer(() => {
                return {type: InternalVedleggkravRenderer}
              })
            }))
            steg.element(new FormattedText("", v => {
              v.customRenderer(() => {
                return {type: LinkComponent}
              })
            }))

            steg.field("epost", new TextField(f => {
              f.label(_("E-postadresse"))
              f.fullBredde()
              f.fieldDescription(Rule.constant(this.realm == 'privat' ? _("For kvittering. Hentet fra det nasjonale kontaktregisteret.") : _("For kvittering.")))
              f.required(_("E-postadressen må være i riktig format, for eksempel: deg@epost.no"))
              f.valid(c => {
                if (!c.getValue()) {
                  return
                }
                if (!ValideringsRegler.gyldigEpost(c.getValue())) {
                  return {literal: _("E-postadressen må være i riktig format, for eksempel: deg@epost.no")}
                }
                return
              })
            }))
            steg.field("telefonnummer", new TextField(f => {
              f.label(_("Telefonnummer"))
              f.visible(Rule.constant(this.realm == "privat"))
              f.fieldDescription(_("Telefonnummer er hentet fra det nasjonale kontaktregisteret."))
              f.required(_("Telefonnummeret må være i riktig format, for eksempel: 12345678 eller +47 12345678"))
              f.valid(c => {
                if (!c.getValue()) {
                  return
                }
                if (!ValideringsRegler.gyldigTelefonnr(c.getValue())) {
                  return {literal: _("Telefonnummeret må være i riktig format, for eksempel: 12345678 eller +47 12345678")}
                }
                return
              })
            }))
          }))
        }))
      })
    })

  public findRealm(): string {
    var currentUrl = this.router.url
    if (currentUrl.includes('privat')) {
      return 'privat';
    } else if (currentUrl.includes('proff')) {
      return 'proff';
    }
    return 'privat';
  }

  handleEvent($event: FormEvent<any>) {
    if ($event.eventType == "sendinn") {
      this.loading.set(true);
      this.sikkerInnsendingService.submitSchema({ body: { uuid: this.myUuid() } })
        .subscribe({
          next: (response) => {
            this.loading.set(false);
            this.router.navigate(
              ['bekreftelse'],
              { relativeTo: this.activatedRoute, queryParamsHandling: 'merge' }
            );
          },
          error: (err) => {
            this.loading.set(false);
            console.error('Kunne ikke sende skjema', err);
            throw err
          }
        });
    }
  }
}
